import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getPublicImgUrl } from '../../../../utils/common';

export default function AppointmentDetailsPage() {
    const chatDivRef = useRef(null);
    const { setIsLoading } = useLoading();
    const MySwal = withReactContent(Swal);
    const { id } = useParams();
    const { userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['patient'] });
    document.title = `Varosa - Patient Appointment No ${id}`;
    const reportFileInputRef = useRef(null);
    const [accordionStatus, setAccordionStatus] = useState({
        one: true,
        two: true,
        three: true,
        four: true,
        five: true,
    });
    const [ appointmentData, setAppointmentData ] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    /* window.Echo.channel('appointment-chat.' + id + '.' + user?.id)
        .listen('AppointmentMessageSent', (e) => {
            setChatMessages(prevMessages => [...prevMessages, e.message]);
            if (!e.message.seen) {
                setUnseenMessagesCount(prevCount => prevCount + 1);
            }
        }); */
    const [chatFormData, setChatFormData] = useState({
        message: '',
        files: [],
    });
    const [chatFormError, setChatFormError] = useState({
        message: '',
        files: '',
    });
    useEffect(() => {
        fetchAppointment();
        fetchChatMessages();
        const timeInterval = 3500;
        const interval = setInterval(() => fetchChatMessages(), timeInterval);
        // if(appointmentData?.booking_status !== 'payment paid'){
        //     clearInterval(interval);
        // }
        return () => clearInterval(interval);
    }, [appointmentData?.booking_status]);
    const fetchAppointment = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}`);
            if (response?.data?.result === 'success') {
                setAppointmentData(response?.data?.data);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const updateCanChat = (newCanChatValue) => {
        setAppointmentData(prevData => ({
            ...prevData,
            appointment: {
                ...prevData.appointment,
                can_chat: newCanChatValue
            }
        }));
    };
    const fetchChatMessages = async () => {
        try {
            // setIsLoading(true);
            const response = await api.get(`/appointment/${id}/chat`);
            if (response?.data?.result === 'success') {
                if(response?.data?.data?.length > 0){
                    updateCanChat(response?.data?.data[0].can_chat || false);
                }
                setChatMessages(response?.data?.data);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching chat messages:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const sendChatMessage = async () => {
        try {
            if(!chatFormData.message && !chatFormData.file){
                setChatFormError({
                    ...chatFormError,
                    message: 'Please enter a message',
                });
                return;
            }
            setChatFormError({
                ...chatFormError,
                message: '',
                files: '',
            });
            setIsLoading(true);
            const response = await api.post(`/appointment/${id}/chat`, chatFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response?.data?.result === 'success') {
                setChatFormData({
                    ...chatFormData,
                    message: '',
                    files: [],
                });
                reportFileInputRef.current.value = '';
                setChatMessages(prevMessages => [...prevMessages, response?.data?.data]);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error sending chat message:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }

    const handleFileUpload = (e) => {
        setChatFormError({
            ...chatFormError,
            files: '',
        });
        const files = Array.from(e.target.files);
        for(let file of files){
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                setChatFormError({
                    ...chatFormError,
                    files: 'Please upload a PDF, PNG, JPEG file.',
                });
                handleRemoveFile();
                return;
            }
            if(file.size > 1 * 1024 * 1024){
                setChatFormError({
                    ...chatFormError,
                    files: 'File size exceeds 1MB. Please choose a smaller file.',
                });
                handleRemoveFile();
                return;
            }
        }
        setChatFormData({ ...chatFormData, files: files });
    };
    const handleRemoveFile = () => {
        reportFileInputRef.current.value = '';
        setChatFormData({ ...chatFormData, files: [] });
        setChatFormError({ ...chatFormError, files: '' });
    };

    const openCancelAppointmentPopop = (id) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to cancel the appointment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                handleCancelAppointment(id);
            }
        });
    }
    const scrollToChatDiv = () => {
      if (chatDivRef.current) {
        chatDivRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setAccordionStatus({ ...accordionStatus, four: true })
      }
    }
    const renderReport = (report) => {
        switch(report.mime_type){
            case 'image/jpeg':
            case 'image/png':
            case 'image/jpg':
                return <><img src={report.url} alt={report.original_name} className="img-fluid" style={{ width: '100px' }} /> <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewReport(report)}></i>
                </span></>;
            case 'application/pdf':
                return <>{report.original_name} <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewReport(report)}></i>
                </span></>;
            default:
                return <>{report.original_name} <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewReport(report)}></i>
                </span></>;
        }
    }
    const viewReport = (report) => {
        MySwal.fire({
            title: 'File View Finder: ' + report.original_name,
            html: `<iframe src="${report.url}" alt="File View Finder: ${report.original_name}" style="width: 100%;height: 600px;"></iframe>`,
            width: '90%',
            showConfirmButton: true,
            confirmButtonText: 'Close',
            customClass: {
                container: '',
            },
        });
    };

    const renderPrescriptionDetails = () => {
        const prescription = appointmentData?.appointment_prescription || {};
        switch (prescription?.prescription_type) {
            case 'physiotherapy':
                return (
                    <>
                        <p className="mb-3">Chief Complaints: {prescription?.chief_complaints}</p>
                        <p className="mb-3">History of Present Illness (HOPI): {prescription?.hopi}</p>
                        <p className="mb-3">Examination: {prescription?.examination}</p>
                        <p className="mb-3">Diagnosis: {prescription?.diagnosis}</p>
                        <p className="mb-3">Management: {prescription?.management}</p>
                    </>
                );
            case 'speech_therapy':
                return (
                    <>
                        <p className="mb-3">Provisional Diagnosis: {prescription?.provisional_diagnosis}</p>
                        <p className="mb-3">Present Concerns: {prescription?.present_concerns}</p>
                        <p className="mb-3">Assessment Findings: {prescription?.assessment_findings}</p>
                        <p className="mb-3">Advice / Home Program: {prescription?.advice_home_program}</p>
                        <p className="mb-3">Recommendations: {prescription?.recommendations}</p>
                        <p className="mb-3">Frequency of Therapy: {prescription?.frequency_of_therapy}</p>
                        <p className="mb-3">Review Date: {prescription?.review_date}</p>
                        <p className="mb-3">Therapy Plan:</p>
                        <ol>
                        {prescription?.therapy_plan?.map((item, index) => (
                            <li key={item.id || index} style={{border: '1px solid #ced4da', backgroundColor: '#e9ecef', padding: '.375rem .75rem', marginBottom: '10px', borderRadius: '10px'}}>
                                {item.instruction}
                            </li>
                        ))}
                        </ol>
                    </>
                );
            case 'diet_prescription':
                return (
                    <>
                        <p className="mb-3">Cereals: {(prescription?.cereals || []).join(', ')}</p>
                        <p className="mb-3">Dal and Dal Products: {prescription?.dal_and_dal_products}</p>
                        <p className="mb-3">Green Leafy Vegetables: {prescription?.green_leafy_vegetables}</p>
                        <p className="mb-3">Other Vegetables: {prescription?.other_vegetables}</p>
                        <p className="mb-3">Fruits: {prescription?.fruits}</p>
                        <p className="mb-3">Fish: {prescription?.fish}</p>
                        <p className="mb-3">Chicken: {prescription?.chicken}</p>
                        <p className="mb-3">Egg: {prescription?.egg}</p>
                        <p className="mb-3">Oil: {prescription?.oil}</p>
                        <p className="mb-3">Milk: {prescription?.milk}</p>
                        <p className="mb-3">Foods to Avoid:</p>
                        <ul>
                        {prescription?.foods_to_avoid?.filter((item) => item?.checked !== false).map((item, index) => (
                            <li key={item.id || index}>{item.item}</li>
                        ))}
                        </ul>
                    </>
                );
            default:
                return (
                    <>
                        <p className="mb-3">Clinical Summary: {prescription?.chief_complaints}</p>
                        <p className="mb-3">History Points: {prescription?.history_points}</p>
                        <p className="mb-3">Lab Findings: {prescription?.lab_findings}</p>
                        <p className="mb-3">Suggested Investigations: {prescription?.suggested_investigations}</p>
                        <p className="mb-3">Special Instructions: {prescription?.special_instructions}</p>
                        <p className="mb-3">Diagnosis: {prescription?.diagnosis}</p>
                        <p className="mb-3">Remarks: {prescription?.remarks}</p>
                        <p className="mb-3">Medication RX:</p>
                        <ol>
                        {prescription?.med_rx_products?.map((item, index) => (
                            <li key={index} style={{border: '1px solid #ced4da', backgroundColor: '#e9ecef', padding: '.375rem .75rem', marginBottom: '10px', borderRadius: '10px'}}>
                                <div className="mb-3">
                                    <label className="form-label">{item.product}</label>
                                    <p className="mb-0">
                                        <span className="fw-bold">Dosage:</span> {item.dosage}
                                    </p>
                                    <p className="mb-0">
                                        <span className="fw-bold">Route:</span> {item.route}
                                    </p>
                                    <p className="mb-0">
                                        <span className="fw-bold">Frequency:</span> {item.frequency}
                                    </p>
                                    <p className="mb-0">
                                        <span className="fw-bold">Schedule:</span> {item.schedule}
                                    </p>
                                    <p className="mb-0">
                                        <span className="fw-bold">Instruction:</span> {item.instruction}
                                    </p>
                                    <p className="mb-0">
                                        <span className="fw-bold">Duration:</span> {item.duration}
                                    </p>
                                </div>
                            </li>
                        ))}
                        </ol>
                    </>
                );
        }
    }
    const handleCancelAppointment = async (appointmentId) => {
        try{
            const response = await api.post(`/appointment/${appointmentId}/cancel`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        }catch(error){
            showError('Error canceling appointment:', error?.response?.data?.message || error.message);
        }
    }

    return (
        <>
            <button className="start-chat-btn" type="button" onClick={scrollToChatDiv}><i className="fas fa-comments"></i> Talk To Doctor</button>
            <div className="container py-5 login-container appointment-detail">
                <div className="row g-3">
                    <div className="col-8">
                        <h2 className="mb-4">Appointment No {id} (Booked at {appointmentData?.appointment?.appointment_date_time})</h2>
                        <small className="text-muted mb-3">Status: {appointmentData?.appointment?.booking_status}</small>
                    </div>
                    <div className="col-4 text-end">
                        <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                    </div>
                    <hr />
                </div>
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="accordion">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingOne" 
                                    onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne" aria-expanded="true" aria-controls="panelsStayOpen-collapseOne">
                                        Doctor Details
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingOne">
                                    <div className="accordion-body">
                                        <div className="row">
                                            <div className="col-12">
                                                <img src={appointmentData?.doctor?.profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} alt={appointmentData?.doctor?.name} className="img-fluid" style={{ width: '250px' }} />
                                                <h5 className="mb-2 mt-2">{appointmentData?.doctor?.name}</h5>
                                                <p className="mb-3">
                                                    Doctor Bio: {appointmentData?.doctor?.bio?.length > 100 ? appointmentData?.doctor?.bio.slice(0, 100) : appointmentData?.doctor?.bio}
                                                    {appointmentData?.doctor?.bio?.length > 100 ? (
                                                        <>
                                                        <span className={`d-none rest-bio`}>{appointmentData?.doctor?.bio.slice(100)}</span>
                                                        &nbsp;&nbsp;
                                                        <button onClick={(e) => { 
                                                            e.preventDefault();
                                                            var elements = document.getElementsByClassName(`rest-bio`);
                                                            if(elements.length > 0){
                                                                if(elements[0].classList.contains("d-none")){
                                                                    elements[0].classList.remove("d-none");
                                                                    e.target.textContent = 'Read Less';
                                                                }else{
                                                                    elements[0].classList.add("d-none");
                                                                    e.target.textContent = 'Read More';
                                                                }
                                                            }
                                                        }} className="btn btn-outline-success btn-sm ml-3">Read More</button>
                                                        </>
                                                    ) : ''}
                                                </p>
                                                <p className="mb-3">Specializations: {appointmentData?.doctor?.specializations.map((specialization) => (
                                                    <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                                ))}</p>
                                                <p className="mb-3">Consultation Fees: <b>&#8377;{appointmentData?.doctor?.consultation_fee}</b></p>
                                                {appointmentData?.appointment?.invoice_url && (
                                                    <button onClick={() => { window.open(appointmentData?.appointment?.invoice_url, '_blank'); }} className="btn btn-primary m-2">View Invoice</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="accordion mt-3">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingThree"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, three: !accordionStatus.three })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.three ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseThree" aria-expanded="false" aria-controls="panelsStayOpen-collapseThree">
                                        Doctor Prescription
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseThree" className={`accordion-collapse collapse ${accordionStatus.three ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingThree">
                                    <div className="accordion-body">
                                        {appointmentData?.appointment?.prescription_url ? (
                                            <>
                                                {appointmentData?.appointment_prescription?.prescription_type_label && (
                                                    <p className="mb-3"><span className="badge bg-primary">{appointmentData?.appointment_prescription?.prescription_type_label}</span></p>
                                                )}
                                                {renderPrescriptionDetails()}
                                                <button onClick={() => { window.open(appointmentData?.appointment?.prescription_url, '_blank'); }} className="btn btn-primary m-2">View Prescription</button>
                                            </>) : (<p className="mb-3">Your Prescription has not generated yet.</p>)
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="accordion mt-3">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingFive"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, five: !accordionStatus.five })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.five ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseFive" aria-expanded="false" aria-controls="panelsStayOpen-collapseFive">
                                        Payment Status
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseFive" className={`accordion-collapse collapse ${accordionStatus.five ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingFive">
                                    <div className="accordion-body">
                                        <p className="mb-3">Payment Status: {appointmentData?.appointment?.payment_status}</p>
                                        <p className="mb-3">Payment Amount: <b>&#8377;{appointmentData?.appointment?.paid_amount}</b></p>
                                        {appointmentData?.appointment?.can_cancel && (
                                            <button onClick={() => { openCancelAppointmentPopop(appointmentData?.appointment?.id); }} className="btn btn-primary">Cancel Appointment</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="accordion mt-3 d-none">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingTwo"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, two: !accordionStatus.two })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.two ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseTwo" aria-expanded="false" aria-controls="panelsStayOpen-collapseTwo">
                                        Medical Details
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseTwo" className={`accordion-collapse collapse ${accordionStatus.two ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingTwo">
                                    <div className="accordion-body">
                                        <p className="mb-3">Patient Problem: {appointmentData?.appointment?.visit_reason}</p>
                                        <p className="mb-3">Patient Name: {appointmentData?.appointment?.patient_name}</p>
                                        <p className="mb-3">Patient Age: {appointmentData?.appointment?.patient_dob}</p>
                                        <p className="mb-3">Patient Gender: {appointmentData?.appointment?.patient_gender}</p>
                                        <p className="mb-3">Patient Contact: {appointmentData?.appointment?.patient_phone_numbers}</p>
                                        <p className="mb-3">Patient Email: {appointmentData?.appointment?.patient_email}</p>
                                        <p className="mb-3">Patient Address: {appointmentData?.appointment?.patient_address_line1}</p>
                                        <p className="mb-3">Patient Address Line 2: {appointmentData?.appointment?.patient_address_line2}</p>
                                        <p className="mb-3">Patient District: {appointmentData?.appointment?.patient_district}</p>
                                        <p className="mb-3">Patient State: {appointmentData?.appointment?.patient_state}</p>
                                        <p className="mb-3">Patient Pincode: {appointmentData?.appointment?.patient_pincode}</p>
                                        <p className="mb-3">Patient Country: {appointmentData?.appointment?.patient_country}</p>
                                        <p className="mb-3">Patient Allergies: {appointmentData?.appointment?.patient_allergies}</p>
                                        <p className="mb-3">Patient Regular Medicines: {appointmentData?.appointment?.patient_regular_medicines}</p>
                                        <p className="mb-3">Patient Reports: {appointmentData?.appointment?.patient_reports?.map((report) => (
                                            <button key={report} onClick={() => viewReport(report)} className="btn btn-primary me-2">View Report</button>
                                        ))}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="accordion" ref={chatDivRef}>
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingFour"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, five: !accordionStatus.five })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.five ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseFour" aria-expanded="false" aria-controls="panelsStayOpen-collapseFour">
                                        Chat with doctor
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseFour" className={`accordion-collapse collapse ${accordionStatus.five ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingFour">
                                    <div className="accordion-body">
                                        <ul className="list-unstyled mb-0">
                                            {chatMessages?.map((message, index, arr) => {
                                                return message.me ? (
                                                <li key={index} className="d-flex mb-4">
                                                    <span className="rounded-circle d-flex align-self-start me-3 shadow-1-strong mt-4">
                                                        <i className="fas fa-user fa-lg" style={{ color: '#000' }}></i>
                                                    </span>
                                                    <div className="card w-100">
                                                        <div className="card-header d-flex justify-content-between p-3">
                                                            <p className="fw-bold mb-0">{message?.user_name}</p>
                                                            <p className="text-muted small mb-0">{message?.created_at}</p>
                                                        </div>
                                                        <div className="card-body">
                                                            {message?.message && (
                                                                <p className="mb-0">{message?.message}</p>
                                                            )}
                                                            {message?.files.length > 0 && (<>
                                                                <hr />
                                                                <div className="mt-1 mb-1">
                                                                    <ul className="list-group">
                                                                        {message?.files.map((file, index) => (
                                                                        <li key={index} className="list-group-item">
                                                                            {renderReport(file)}
                                                                        </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>)}
                                                        </div>
                                                    </div>
                                                </li>
                                                ):(
                                                <li key={index} className="d-flex mb-4">
                                                    <div className="card w-100">
                                                        <div className="card-header d-flex justify-content-between p-3">
                                                            <p className="fw-bold mb-0">{message?.user_name}</p>
                                                            <p className="text-muted small mb-0">{message?.created_at}</p>
                                                        </div>
                                                        <div className="card-body">
                                                            {message?.message && (
                                                                <p className="mb-0">{message?.message}</p>
                                                            )}
                                                            {message?.files.length > 0 && (<>
                                                                <hr />
                                                                <div className="mt-1 mb-1">
                                                                    <ul className="list-group">
                                                                        {message?.files.map((file, index) => (
                                                                        <li key={index} className="list-group-item">
                                                                            {renderReport(file)}
                                                                        </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>)}
                                                        </div>
                                                        {message?.link && <div className="card-footer text-end" dangerouslySetInnerHTML={{ __html: message?.link }}></div>}
                                                    </div>
                                                    <span className="rounded-circle d-flex align-self-start ms-3 shadow-1-strong mt-4">
                                                        <i className="fas fa-user-md fa-lg" style={{ color: '#000' }}></i>
                                                    </span>
                                                </li>
                                                )
                                            })}
                                            <li className="bg-white">
                                                {appointmentData?.appointment?.can_chat ? (
                                                <><hr />
                                                <div className="form-outline">
                                                    <label className="form-label" htmlFor="message">Message</label>
                                                    <textarea 
                                                        className="form-control bg-body-tertiary" 
                                                        rows="4" 
                                                        name="message" 
                                                        id="message"
                                                        value={chatFormData.message}
                                                        onChange={(e) => setChatFormData({ ...chatFormData, message: e.target.value })}
                                                    ></textarea>
                                                    {chatFormError.message && <div className="invalid-feedback d-block mb-2">Please enter a message.</div>}
                                                    <label className="form-label mt-2 mb-2" htmlFor="file">Report Files</label>
                                                    <div className="input-group mb-1">
                                                        <input
                                                            type="file"
                                                            name="file"
                                                            id="file"
                                                            className="form-control py-2 border-primary"
                                                            onChange={handleFileUpload}
                                                            accept=".pdf,.jpg,.jpeg,.png" 
                                                            ref={reportFileInputRef}
                                                        />
                                                        <button className="btn btn-primary" type="button" onClick={handleRemoveFile}>Clear</button>
                                                    </div>
                                                    <div className="text-muted mt-1 mb-1">
                                                        <small>Note: PDF, JPEG, JPG, or PNG. Max size 1MB per file.</small>
                                                    </div>
                                                    {chatFormError.files && <div className="invalid-feedback d-block mt-1 mb-1">{chatFormError.files}</div>}
                                                    <div className="text-end">
                                                        <button type="button" className="btn btn-info btn-rounded mt-3" onClick={sendChatMessage}>Send</button>
                                                    </div>
                                                </div></>
                                                ) : (<p className="text-muted text-center">Chat is Disabled</p>)}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}